import React, { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './review-documents.css';
import { useLocation } from 'react-router-dom';
import HomeSousCritere from '../Composents/Home_Sous_Critere';
import IconSelectionCritere from '../Composents/Icon_Selection_Critère';
const { getVisibleSousCritereEntries } = require('../shared/progress');
const noop = () => {};
const noSave = () => Promise.resolve(false);
const BASE = '/backend/salesforce/candidature/review';
const REVIEW_SECTION_ID = 'responsable-avis';

// Critère propre au lecteur professeur : jamais enregistré dans le dossier étudiant.
// Le dossier reçu ne contient que la filière (vœu) soumise à ce responsable.
function buildReviewView(dossier, avis, commentaire) {
  const period = dossier.data[0];
  const criteres = (period.criteres || []).filter(item => Number(item.id) !== 50);
  const reviewSection = {
    id: REVIEW_SECTION_ID,
    nom: 'Avis du responsable',
    pourcentage: (Number(Boolean(avis)) + Number(Boolean(commentaire.trim()))) * 50,
    questions: [
      { id: 'responsable-avis-valeur', type: 'select', value: avis, required: 'required' },
      { id: 'responsable-commentaire-valeur', type: 'textarea', value: commentaire, required: 'required' }
    ]
  };
  const reviewCriteres = [
    ...criteres,
    { id: 'responsable', nom: 'Avis du responsable', sousCriteres: [reviewSection] }
  ];
  return { ...dossier, data: [{ ...period, criteres: reviewCriteres }] };
}


function ReviewHeaderDownload({ busy, onDownload }) {
  const header = document.querySelector('.header-content');
  useLayoutEffect(() => {
    if (!header) return;
    header.classList.add('review-header');
    return () => header.classList.remove('review-header');
  }, [header]);
  return header ? createPortal(
    <div className="review-document-tools">
      <button type="button" onClick={onDownload} disabled={busy}>
        {busy ? 'Préparation du ZIP…' : 'Télécharger toutes les pièces (ZIP)'}
      </button>
    </div>, header
  ) : null;
}

export default function CandidatureReview() {
  const location = useLocation();
  const secret = location.hash.slice(1);
  const [loaded, setLoaded] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [closed, setClosed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);
  const [zipError, setZipError] = useState('');
  const [avis, setAvis] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [main, setMain] = useState(1);
  const [critere, setCritere] = useState(0);
  const [sousCritere, setSousCritere] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoaded(null); setClosed(false); setError(''); setMessage(''); setMain(1);
    fetch(`${BASE}/dossier`, { headers: { 'x-review-token': secret }, cache: 'no-store', signal: controller.signal })
      .then(async response => {
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.message || 'Le dossier ne peut pas être ouvert.');
        if (controller.signal.aborted) return;
        if (payload.closed) { setClosed(true); setMessage(payload.message); return; }
        setLoaded(payload); setAvis(payload.avis); setCommentaire(payload.commentaire);
      }).catch(e => { if (!controller.signal.aborted) setError(e.message); });
    return () => controller.abort();
  }, [secret]);

  // Chaque lien vise un seul vœu : l'en-tête nomme la filière à examiner.
  useEffect(() => {
    const titre = document.getElementById('titre');
    if (!titre || !loaded) return undefined;
    const previous = titre.textContent;
    const filiere = [loaded.niveau, loaded.filiere].filter(Boolean).join(' - ');
    titre.textContent = filiere ? `Avis du responsable : ${filiere}` : 'Avis du responsable';
    return () => { titre.textContent = previous; };
  }, [loaded]);

  const downloadDocuments = async () => {
    if (zipBusy) return;
    setZipBusy(true); setZipError('');
    try {
      const response = await fetch(`${BASE}/documents.zip`, { headers: { 'x-review-token': secret }, cache: 'no-store' });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || 'Impossible de télécharger les pièces.');
      }
      const url = URL.createObjectURL(await response.blob());
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = 'pieces-candidature.zip';
      document.body.appendChild(anchor); anchor.click(); anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) { setZipError(error.message); }
    finally { setZipBusy(false); }
  };

  const submit = async event => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError(''); setMessage('');
    try {
      const response = await fetch(`${BASE}/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'x-review-token': secret },
        body: JSON.stringify({ avis, commentaire })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.message || 'L’envoi a échoué. Vous pouvez réessayer.');
      setMessage(payload.message);
      if (!payload.dryRun) setClosed(true);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  if (closed) return <main className="containerConfirmation"><h1>Avis du responsable</h1><p role="status">{message}</p></main>;
  if (!loaded) return <main className="containerConfirmation"><p role={error ? 'alert' : 'status'}>{error || 'Chargement du dossier…'}</p></main>;
  const data = buildReviewView(loaded.dossier, avis, commentaire);
  const period = data.data[0];
  const visible = getVisibleSousCritereEntries(period);
  const currentSection = period.criteres[critere]?.sousCriteres[sousCritere];
  const isReviewSection = currentSection?.id === REVIEW_SECTION_ID;
  const questions = isReviewSection ? [] : currentSection?.questions || [];
  const reviewForm = <form onSubmit={submit}>
    <div className="question">
      <label className="questionTitle" htmlFor="responsable-avis">Avis du responsable *</label>
      <select id="responsable-avis" className="text" required value={avis} disabled={busy} onChange={e => setAvis(e.target.value)}>
        <option value="">Sélectionnez un avis</option>
        {loaded.options.map(value => <option key={value} value={value}>{value}</option>)}
      </select>
    </div>
    <div className="question">
      <label className="questionTitle" htmlFor="responsable-commentaire">Commentaire *</label>
      <textarea id="responsable-commentaire" className="text" rows={6} required maxLength={loaded.maxLength} value={commentaire} disabled={busy} onChange={e => setCommentaire(e.target.value)} />
    </div>
    {error && <p role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    <div className="BoutonsNavMain"><button id="BoutonSoumission" disabled={busy || !avis || !commentaire.trim()}><p>{busy ? 'Envoi en cours…' : 'Transmettre l’avis'}</p></button></div>
  </form>;

  return <>
    <ReviewHeaderDownload busy={zipBusy} onDownload={downloadDocuments} />
    {zipError && <p className="review-document-error" role="alert">{zipError}</p>}
    {main !== 2 ? <main id="MenuSelectionCritère" className="MenuSelectionCritere">
      {period.criteres.map((item, index) => visible.some(entry => entry.critereIndex === index) &&
        <IconSelectionCritere key={item.id} critereInfo={item} data={data} getNumberFiliereActif={0} Lang="fr" readOnly
          actionLabel={section => section.id === REVIEW_SECTION_ID ? 'Compléter' : 'Consulter'}
          onSelect={id => { setCritere(index); setSousCritere(item.sousCriteres.findIndex(s => s.id === id)); setMain(2); }} />)}
    </main> : <main id="HomeSousCritere" className="HomeSousCritere">
      <HomeSousCritere key={`${critere}-${sousCritere}`} data={data} souscritereInfo={questions}
        getMainActifNum={2} setMainActifNum={setMain} getNumberFiliereActif={0}
        getNumberCritereActif={critere} setNumberCritereActif={setCritere}
        getNumberSousCritereActif={sousCritere} setNumberSousCritereActif={setSousCritere}
        criteres={period.criteres} Lang="fr" filiere={period.nom}
        updateQuestionValue={noop} updateQuestionVue={noop} updateQuestionVisible={noop}
        updateFiltreGeneral={noop} updateQuestionSearchCode={noop} updateQuestionSearchValueName={noop}
        updateQuestionSelectPays={noop} setQuestionValueToTrue={noop} setEstimationTestSelection={noop}
        sendDataToServer={noSave} triggerSave={noSave} checkPaymentExists={noSave} recordNav={noop}
        isDisabled2={false} paymentExists={false} readOnly reviewToken={secret}>
        {isReviewSection && reviewForm}
      </HomeSousCritere>
    </main>}
  </>;
}
